import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';

export default function Assistant() {
  const { t } = useI18n();
  const SUGGESTIONS = t('assistant.suggestions');
  const [messages, setMessages] = useState([]); // { role, content }
  const [input,    setInput]    = useState('');
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState('');
  const endRef = useRef(null);
  const navigate = useNavigate();

  const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user')?.content || '';
  const escalate = () => navigate('/tickets', { state: { draft: lastUserMsg } });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setError('');
    const next = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const { data } = await api.post('/assistant/chat', { messages: next }, { timeout: 60000 });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setError(err.response?.data?.message || t('assistant.errUnavailable'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title={t('assistant.title')} subtitle={t('assistant.subtitle')} />
      <div className="px-5 md:px-0 pb-6">
        <div className="card flex flex-col h-[calc(100vh-16rem)] min-h-[24rem]">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center gap-4 px-4">
                <div className="grid place-items-center w-12 h-12 rounded-2xl bg-glk-green/10 text-glk-green">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" /></svg>
                </div>
                <p className="text-sm text-gray-500 max-w-xs">{t('assistant.lead')}</p>
                <div className="flex flex-col gap-2 w-full max-w-sm">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="text-left text-sm px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:border-glk-green/40 hover:text-glk-dark transition-colors cursor-pointer">
                      {s}
                    </button>
                  ))}
                </div>
                <button onClick={() => navigate('/faq')} className="text-xs font-medium text-glk-green hover:underline cursor-pointer">
                  {t('assistant.faqLink')}
                </button>
              </div>
            ) : (
              messages.map((m, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.role === 'user' ? 'bg-glk-green text-white rounded-br-sm' : 'bg-gray-100 text-glk-dark rounded-bl-sm'
                  }`}>
                    {m.content}
                  </div>
                </motion.div>
              ))
            )}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-400 px-4 py-3 rounded-2xl rounded-bl-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {error && <p className="px-4 pb-2 text-xs text-red-600">{error}</p>}

          {/* Composer */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-gray-200 p-3 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder={t('assistant.placeholder')}
              className="input flex-1 resize-none max-h-32"
            />
            <button type="submit" disabled={busy || !input.trim()} className="btn-primary !px-3.5 shrink-0" aria-label={t('assistant.escalate')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>
            </button>
          </form>
        </div>

        <div className="mt-3 px-1 flex items-center justify-between gap-3">
          <p className="text-[11px] text-gray-400">
            {t('assistant.disclaimer')}
          </p>
          {messages.length > 0 && (
            <button onClick={escalate} className="shrink-0 text-xs font-medium text-glk-green hover:underline cursor-pointer">
              {t('assistant.escalate')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
